import React, { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Phone, MessageCircle, Mail, MapPin, Menu, X, Clock } from "lucide-react";
import { Monogram } from "./Monogram";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { useLang } from "../i18n/LanguageContext";
import { OFFICE, whatsappUrl, telUrl, mailUrl, mapUrl } from "../lib/office";

const navItems = [
  { to: "/", key: "home" },
  { to: "/cabinet", key: "cabinet" },
  { to: "/services", key: "services" },
  { to: "/documents", key: "documents" },
  { to: "/rendez-vous", key: "appointment" },
  { to: "/contact", key: "contact" },
  { to: "/faq", key: "faq" },
];

export default function Layout({ children }) {
  const { t, lang } = useLang();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex flex-col bg-base text-charcoal">
      {/* Top info bar */}
      <div className="hidden md:block border-b border-border-light bg-surface-muted">
        <div className="container-notary flex items-center justify-between h-9 text-[11px] text-charcoal-muted">
          <div className="flex items-center gap-5">
            <span className="inline-flex items-center gap-2">
              <MapPin className="w-3 h-3 text-gold" />
              {t("office.address")}
            </span>
            <span className="inline-flex items-center gap-2">
              <Clock className="w-3 h-3 text-gold" />
              {t("office.hours")}
            </span>
          </div>
          <div className="flex items-center gap-5">
            <a href={telUrl(true)} className="hover:text-gold inline-flex items-center gap-2">
              <Phone className="w-3 h-3 text-gold" />
              {OFFICE.phoneFixed}
            </a>
            <a href={mailUrl()} className="hover:text-gold inline-flex items-center gap-2">
              <Mail className="w-3 h-3 text-gold" />
              {OFFICE.email}
            </a>
          </div>
        </div>
      </div>

      {/* Header */}
      <header className="sticky top-0 z-40 bg-base/90 backdrop-blur-md border-b border-border-light">
        <div className="container-notary flex items-center justify-between h-20">
          <Link to="/" aria-label="home" data-testid="header-logo-link">
            <Monogram size={42} />
          </Link>
          <nav className="hidden lg:flex items-center gap-7" data-testid="main-nav">
            {navItems.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.to === "/"}
                className={({ isActive }) =>
                  `text-sm tracking-wide transition-colors ${
                    isActive ? "text-gold" : "text-charcoal hover:text-gold"
                  }`
                }
                data-testid={`nav-${it.key}`}
              >
                {t(`nav.${it.key}`)}
              </NavLink>
            ))}
          </nav>
          <div className="hidden md:flex items-center gap-4">
            <LanguageSwitcher />
            <Link to="/rendez-vous" className="btn-gold" data-testid="header-cta-appointment">
              {t("common.bookAppointment")}
            </Link>
          </div>
          <button
            className="lg:hidden inline-flex items-center justify-center w-10 h-10 border border-border-light"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="menu"
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile drawer */}
        {mobileOpen && (
          <div className="lg:hidden border-t border-border-light bg-surface" data-testid="mobile-menu">
            <div className="container-notary py-6 flex flex-col gap-4">
              {navItems.map((it) => (
                <NavLink
                  key={it.to}
                  to={it.to}
                  end={it.to === "/"}
                  className={({ isActive }) =>
                    `text-sm py-2 border-b border-border-light ${
                      isActive ? "text-gold" : "text-charcoal"
                    }`
                  }
                  data-testid={`mobile-nav-${it.key}`}
                >
                  {t(`nav.${it.key}`)}
                </NavLink>
              ))}
              <div className="flex items-center justify-between pt-2">
                <LanguageSwitcher testId="mobile-language-switcher" />
                <Link to="/rendez-vous" className="btn-gold" data-testid="mobile-cta-appointment">
                  {t("common.bookAppointment")}
                </Link>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Main content */}
      <main className="flex-1 pb-28 md:pb-0">{children}</main>

      {/* Footer */}
      <footer className="border-t border-border-light bg-surface-muted mt-16">
        <div className="container-notary py-16 grid grid-cols-1 md:grid-cols-4 gap-10">
          <div>
            <Monogram size={40} />
            <p className="mt-6 text-sm text-charcoal-muted leading-relaxed max-w-xs">
              {t("footer.line")}
            </p>
          </div>
          <div>
            <div className="overline mb-4">{t("footer.quickLinks")}</div>
            <ul className="space-y-2 text-sm">
              {navItems.map((it) => (
                <li key={it.to}>
                  <Link to={it.to} className="text-charcoal hover:text-gold transition-colors">
                    {t(`nav.${it.key}`)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="overline mb-4">{t("footer.contact")}</div>
            <ul className="space-y-2 text-sm text-charcoal-muted">
              <li className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-gold mt-0.5 shrink-0" />
                <span>{t("office.address")}</span>
              </li>
              <li>
                <a className="inline-flex items-center gap-2 hover:text-gold" href={telUrl(true)}>
                  <Phone className="w-4 h-4 text-gold" /> {OFFICE.phoneFixed}
                </a>
              </li>
              <li>
                <a className="inline-flex items-center gap-2 hover:text-gold" href={telUrl(false)}>
                  <MessageCircle className="w-4 h-4 text-gold" /> {OFFICE.phoneMobile}
                </a>
              </li>
              <li>
                <a className="inline-flex items-center gap-2 hover:text-gold" href={mailUrl()}>
                  <Mail className="w-4 h-4 text-gold" /> {OFFICE.email}
                </a>
              </li>
              <li className="inline-flex items-center gap-2">
                <Clock className="w-4 h-4 text-gold" /> {t("office.hours")}
              </li>
            </ul>
          </div>
          <div>
            <div className="overline mb-4">{t("footer.legal")}</div>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/mentions-legales" className="text-charcoal hover:text-gold">
                  {t("nav.legal")}
                </Link>
              </li>
              <li>
                <Link to="/confidentialite" className="text-charcoal hover:text-gold">
                  {t("nav.privacy")}
                </Link>
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-border-light">
          <div className="container-notary py-5 text-xs text-charcoal-muted text-center">
            {t("footer.copyright")}
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp button (desktop + mobile) */}
      <a
        href={whatsappUrl(t("whatsappMessages.general"))}
        target="_blank"
        rel="noopener noreferrer"
        className="whatsapp-float no-print fixed bottom-24 md:bottom-8 end-6 z-40 inline-flex items-center justify-center w-12 h-12 rounded-full bg-charcoal hover:bg-gold text-white shadow-[0_8px_24px_rgba(0,0,0,0.12)] transition-colors"
        aria-label="WhatsApp"
        data-testid="whatsapp-float-button"
      >
        <MessageCircle className="w-5 h-5" />
      </a>

      {/* Sticky mobile CTA bar */}
      <div
        className="sticky-mobile-cta no-print md:hidden fixed bottom-0 inset-x-0 z-40 bg-surface/95 backdrop-blur-md border-t border-border-light"
        data-testid="sticky-mobile-cta"
      >
        <div className="grid grid-cols-3">
          <a
            href={telUrl(false)}
            className="flex flex-col items-center justify-center py-3 gap-1 text-charcoal hover:text-gold border-e border-border-light"
            data-testid="mobile-cta-call"
          >
            <Phone className="w-5 h-5 text-gold" />
            <span className="text-[10px] uppercase tracking-[0.18em]">{t("common.call")}</span>
          </a>
          <a
            href={whatsappUrl(t("whatsappMessages.general"))}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col items-center justify-center py-3 gap-1 text-charcoal hover:text-gold border-e border-border-light"
            data-testid="mobile-cta-whatsapp"
          >
            <MessageCircle className="w-5 h-5 text-gold" />
            <span className="text-[10px] uppercase tracking-[0.18em]">{t("common.whatsapp")}</span>
          </a>
          <a
            href={mapUrl()}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col items-center justify-center py-3 gap-1 text-charcoal hover:text-gold"
            data-testid="mobile-cta-itinerary"
          >
            <MapPin className="w-5 h-5 text-gold" />
            <span className="text-[10px] uppercase tracking-[0.18em]">{t("common.itinerary")}</span>
          </a>
        </div>
      </div>
    </div>
  );
}
